
import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Wallet, DollarSign, Plus, RefreshCw } from 'lucide-react';
import { getWalletBalance, topUpWallet } from '@/services/stripeService';
import { useLocation } from 'react-router-dom';
import { toast } from "@/components/ui/use-toast";

interface WalletManagerProps {
  onWalletUpdated?: (balance: number) => void;
}

const WalletManager: React.FC<WalletManagerProps> = ({ onWalletUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [topUpAmount, setTopUpAmount] = useState('5.00');
  const [walletBalance, setWalletBalance] = useState(0);
  const [isTopUpDialogOpen, setIsTopUpDialogOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchWalletBalance = async () => {
      setLoading(true);
      const { balance } = await getWalletBalance();
      setWalletBalance(balance);
      if (onWalletUpdated) {
        onWalletUpdated(balance);
      }
      setLoading(false);
    };
    
    fetchWalletBalance();
    
    // Check for success or canceled params in URL
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('success') === 'true') {
      toast({
        title: "Top-up Successful",
        description: "Your wallet has been topped up successfully!",
      });
      fetchWalletBalance();
    } else if (searchParams.get('canceled') === 'true') {
      toast({
        title: "Top-up Canceled",
        description: "Your wallet top-up was canceled. You can try again anytime.",
        variant: "destructive"
      });
    }
  }, [location.search, onWalletUpdated]);

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount to top up your wallet.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    await topUpWallet(amount);
    setIsTopUpDialogOpen(false);
    setLoading(false);
  };

  if (loading && walletBalance === 0) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-12 w-1/3 mb-4" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-10 w-full" />
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Wallet className="mr-2 h-5 w-5 text-primary" /> 
          Micropayment Wallet
        </CardTitle>
        <CardDescription>
          Pay for individual articles without a subscription
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
          <p className="text-3xl font-bold text-primary">${walletBalance.toFixed(2)}</p>
        </div>
        
        {walletBalance < 1 && (
          <div className="flex items-start space-x-4 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 mb-4">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-300">Low Balance</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Your wallet balance is low. Top up to continue enjoying instant article purchases.
              </p>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Quick Actions</h4>
          <Button 
            onClick={() => setIsTopUpDialogOpen(true)} 
            className="w-full justify-start"
            variant="outline"
            disabled={loading}
          >
            <Plus className="mr-2 h-4 w-4" /> Top Up Balance
          </Button>
          {/* Add other wallet actions here */}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col space-y-2">
        <p className="text-xs text-muted-foreground">
          Your wallet is used for micropayments to purchase individual articles. 
          The minimum top-up amount is $5.00.
        </p>
      </CardFooter>
      
      {/* Top-up Dialog */}
      <Dialog open={isTopUpDialogOpen} onOpenChange={setIsTopUpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Top Up Your Wallet</DialogTitle>
            <DialogDescription>
              Add funds to your micropayment wallet to continue enjoying articles.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount" className="text-right">
                Amount
              </Label>
              <div className="relative col-span-3">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="pl-10"
                  placeholder="10.00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div></div>
              <div className="col-span-3 grid grid-cols-3 gap-2">
                {[5, 10, 20].map((amount) => (
                  <Button 
                    key={amount} 
                    type="button" 
                    variant="outline" 
                    onClick={() => setTopUpAmount(amount.toString())}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleTopUp} disabled={loading}>
              {loading ? "Processing..." : "Top Up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WalletManager;
